import { useMemo, useCallback, useEffect, useRef, type ReactNode } from "react";
import { cn, Skeleton, Checkbox } from "@cieda/djayma-ui";
import { motion, AnimatePresence } from "framer-motion";
import {
  Pencil,
  Eye,
  Star,
  Boxes,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Trash2,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import type {
  PaginationState,
  SortState,
} from "@/components/admin/shared";
import {
  BRAND_LABEL_MAP,
  CATEGORY_LABEL_MAP,
  type Product,
} from "@/lib/validation/product";
import { ProductImage } from "./ProductImage";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SelectionApi {
  readonly selectedIds: ReadonlySet<string>;
  readonly isAllPageSelected: boolean;
  readonly isSelected: (id: string) => boolean;
  readonly toggle: (id: string) => void;
  readonly toggleAll: (products: readonly Product[]) => void;
}

export interface ProductTableProps {
  readonly products: readonly Product[];
  readonly pagination?: PaginationState;
  readonly onPageChange?: (page: number) => void;
  readonly onSort?: (sort: SortState) => void;
  readonly onEdit: (product: Product) => void;
  readonly onView: (product: Product) => void;
  /**
   * Optional soft-delete (désactiver) handler — when provided, renders a
   * trash button in the row actions. Parent should confirm the action
   * before calling the /api/admin/products/[id] DELETE endpoint.
   */
  readonly onDelete?: (product: Product) => void;
  /** Optional stock drawer opener (Slice 1B). */
  readonly onStockDrawer?: (product: Product) => void;
  readonly isLoading: boolean;
  readonly currentSort?: SortState | null;
  // Slice 1B additions — all optional so existing usages/tests still pass.
  readonly selection?: SelectionApi;
  readonly focusedRowIndex?: number;
  readonly onFocusedRowChange?: (index: number) => void;
  readonly flashingRowIds?: ReadonlySet<string>;
  readonly activeEditors?: ReadonlyMap<
    string,
    { readonly name: string; readonly color: string }
  >;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FCFA = new Intl.NumberFormat("fr-SN", {
  style: "decimal",
  maximumFractionDigits: 0,
});

function formatFCFA(amount: number): string {
  return `${FCFA.format(amount)} FCFA`;
}

const DEFAULT_LOW_STOCK_THRESHOLD = 10;

type StockStatus = "out" | "low" | "ok";

function readStockStatus(product: Product): StockStatus {
  // Read model may surface stock_status; fall back to numeric comparison.
  const raw = (product as unknown as { stock_status?: string }).stock_status;
  if (raw === "out" || raw === "out_of_stock") return "out";
  if (raw === "low" || raw === "low_stock") return "low";
  if (raw === "ok" || raw === "in_stock") return "ok";

  const threshold =
    (product as unknown as { low_stock_threshold?: number | null })
      .low_stock_threshold ?? DEFAULT_LOW_STOCK_THRESHOLD;
  if (product.stock === 0) return "out";
  if (product.stock <= threshold) return "low";
  return "ok";
}

function readThreshold(product: Product): {
  readonly value: number;
  readonly isCustom: boolean;
} {
  const custom = (
    product as unknown as { low_stock_threshold?: number | null }
  ).low_stock_threshold;
  if (custom !== undefined && custom !== null) {
    return { value: custom, isCustom: true };
  }
  return { value: DEFAULT_LOW_STOCK_THRESHOLD, isCustom: false };
}

function StockCell({ product }: { product: Product }): ReactNode {
  const status = readStockStatus(product);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        status === "out" && "bg-red-100 text-red-800 border border-red-200",
        status === "low" && "bg-amber-100 text-amber-800 border border-amber-200",
        status === "ok" &&
          "bg-emerald-100 text-emerald-800 border border-emerald-200",
      )}
    >
      {product.stock}
    </span>
  );
}

function BrandBadge({ brand }: { brand: string }): ReactNode {
  const label = BRAND_LABEL_MAP[brand] ?? brand;
  return (
    <span className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-xs font-medium text-stone-700">
      {label}
    </span>
  );
}

function StatusBadge({ active }: { active: boolean }): ReactNode {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        active
          ? "border-emerald-200 bg-emerald-100 text-emerald-800"
          : "border-stone-200 bg-stone-100 text-stone-500",
      )}
    >
      {active ? "Actif" : "Inactif"}
    </span>
  );
}

function ThresholdCell({ product }: { product: Product }): ReactNode {
  const { value, isCustom } = readThreshold(product);
  return (
    <span className="text-xs text-stone-500">
      Seuil: {value} ({isCustom ? "personnalisé" : "défaut"})
    </span>
  );
}

function PresenceAvatar({
  editor,
}: {
  editor: { readonly name: string; readonly color: string };
}): ReactNode {
  return (
    <span
      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-white"
      style={{ backgroundColor: editor.color }}
      title={`${editor.name} est en train de modifier ce produit`}
      aria-label={`${editor.name} est en train de modifier ce produit`}
    >
      {editor.name.charAt(0).toUpperCase()}
    </span>
  );
}

interface ColumnConfig {
  readonly id: string;
  readonly header: string;
  readonly sortable?: boolean;
  readonly mobileVisible?: boolean;
}

const COLUMNS: readonly ColumnConfig[] = [
  { id: "image", header: "", mobileVisible: false },
  { id: "name", header: "Nom", sortable: true },
  { id: "sku", header: "SKU", sortable: true, mobileVisible: false },
  { id: "brand", header: "Marque" },
  { id: "category", header: "Catégorie", mobileVisible: false },
  { id: "price_fcfa", header: "Prix TTC", sortable: true },
  { id: "stock", header: "Stock", sortable: true },
  { id: "threshold", header: "Seuil", mobileVisible: false },
  { id: "status", header: "Statut", mobileVisible: false },
  { id: "updated_at", header: "Maj", sortable: true, mobileVisible: false },
  { id: "actions", header: "", mobileVisible: false },
] as const;

// ---------------------------------------------------------------------------
// Sortable header cell
// ---------------------------------------------------------------------------

function SortableHeader({
  column,
  currentSort,
  onSort,
}: {
  column: ColumnConfig;
  currentSort: SortState | null | undefined;
  onSort?: (sort: SortState) => void;
}) {
  const isActive = currentSort?.column === column.id;
  const direction = isActive ? currentSort?.direction : null;

  const handleClick = () => {
    if (!column.sortable || !onSort) return;
    const nextDir = direction === "asc" ? "desc" : "asc";
    onSort({ column: column.id, direction: nextDir });
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTableCellElement>) => {
    if (!column.sortable || !onSort) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <th
      scope="col"
      aria-sort={
        !column.sortable
          ? undefined
          : direction === "asc"
            ? "ascending"
            : direction === "desc"
              ? "descending"
              : "none"
      }
      tabIndex={column.sortable ? 0 : undefined}
      onClick={column.sortable ? handleClick : undefined}
      onKeyDown={column.sortable ? handleKey : undefined}
      className={cn(
        "whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500",
        column.sortable &&
          "cursor-pointer select-none hover:text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2",
      )}
    >
      <span className="inline-flex items-center gap-1">
        {column.header}
        {column.sortable && (
          <span className="inline-flex flex-col" aria-hidden="true">
            <ChevronUp
              className={cn(
                "h-3 w-3 -mb-0.5",
                direction === "asc" ? "text-amber-600" : "text-stone-300",
              )}
            />
            <ChevronDown
              className={cn(
                "h-3 w-3 -mt-0.5",
                direction === "desc" ? "text-amber-600" : "text-stone-300",
              )}
            />
          </span>
        )}
      </span>
    </th>
  );
}

// ---------------------------------------------------------------------------
// Skeleton + pagination
// ---------------------------------------------------------------------------

function TableSkeleton({ columns, rows = 5 }: { columns: number; rows?: number }) {
  return (
    <>
      <div className="hidden md:block">
        <div className="overflow-hidden rounded-lg border border-stone-200">
          <table className="w-full">
            <thead className="bg-stone-50">
              <tr>
                {Array.from({ length: columns }).map((_, i) => (
                  <th key={i} className="px-4 py-3">
                    <Skeleton className="h-4 w-20" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rows }).map((_, r) => (
                <tr key={r} className="border-t border-stone-100">
                  {Array.from({ length: columns }).map((_, c) => (
                    <td key={c} className="px-4 py-3">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="space-y-3 md:hidden">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="rounded-lg border border-stone-200 p-4">
            <Skeleton className="mb-2 h-4 w-32" />
            <Skeleton className="mb-1 h-3 w-24" />
            <Skeleton className="h-3 w-20" />
          </div>
        ))}
      </div>
    </>
  );
}

function PaginationControls({
  pagination,
  onPageChange,
}: {
  pagination: PaginationState;
  onPageChange: (page: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));
  const canPrev = pagination.page > 1;
  const canNext = pagination.page < totalPages;
  const start = (pagination.page - 1) * pagination.limit + 1;
  const end = Math.min(pagination.page * pagination.limit, pagination.total);
  return (
    <div className="flex items-center justify-between px-1 py-3">
      <p className="text-xs text-stone-500">
        {start}-{end} sur {new Intl.NumberFormat("fr-SN").format(pagination.total)}
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={!canPrev}
          onClick={() => onPageChange(pagination.page - 1)}
          className="gap-1"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Précédent</span>
        </Button>
        <span className="px-2 text-xs text-stone-500">
          {pagination.page}/{totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={!canNext}
          onClick={() => onPageChange(pagination.page + 1)}
          className="gap-1"
        >
          <span className="hidden sm:inline">Suivant</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ProductTable({
  products,
  pagination,
  onPageChange,
  onSort,
  onEdit,
  onView,
  onDelete,
  onStockDrawer,
  isLoading,
  currentSort,
  selection,
  focusedRowIndex,
  onFocusedRowChange,
  flashingRowIds,
  activeEditors,
}: ProductTableProps) {
  const selectable = !!selection;
  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => c.id !== "threshold" || selectable),
    [selectable],
  );

  const handleToggleAll = useCallback(() => {
    selection?.toggleAll(products);
  }, [selection, products]);

  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  // Scroll focused row into view when it changes
  useEffect(() => {
    if (focusedRowIndex == null) return;
    const focused = products[focusedRowIndex];
    if (!focused) return;
    const node = rowRefs.current.get(focused.id);
    if (node && "scrollIntoView" in node) {
      node.scrollIntoView({ block: "nearest" });
    }
  }, [focusedRowIndex, products]);

  // Loading state
  if (isLoading) {
    return (
      <TableSkeleton
        columns={visibleColumns.length + (selectable ? 1 : 0)}
      />
    );
  }

  // Empty state
  if (products.length === 0) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white py-12 text-center">
        <p className="text-sm text-stone-400">Aucun produit trouvé.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Desktop table */}
      <div className="hidden overflow-x-auto md:block">
        <div className="overflow-hidden rounded-lg border border-stone-200">
          <table
            className="w-full"
            role="grid"
            aria-label="Tableau des produits"
          >
            <thead className="sticky top-0 z-10 bg-stone-50 shadow-sm">
              <tr>
                {selectable && (
                  <th
                    scope="col"
                    className="hidden w-10 px-4 py-3 md:table-cell"
                  >
                    <Checkbox
                      checked={selection!.isAllPageSelected}
                      onCheckedChange={handleToggleAll}
                      aria-label="Tout sélectionner"
                    />
                  </th>
                )}
                {visibleColumns.map((col) => (
                  <SortableHeader
                    key={col.id}
                    column={col}
                    currentSort={currentSort}
                    onSort={onSort}
                  />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white">
              {products.map((row, idx) => {
                const isSelected = selection?.isSelected(row.id) ?? false;
                const isFocused = focusedRowIndex === idx;
                const isFlashing = flashingRowIds?.has(row.id) ?? false;
                const editor = activeEditors?.get(row.id);

                return (
                  <motion.tr
                    key={row.id}
                    ref={(node) => {
                      if (node) rowRefs.current.set(row.id, node);
                      else rowRefs.current.delete(row.id);
                    }}
                    role="row"
                    aria-selected={selectable ? isSelected : undefined}
                    onClick={() => onFocusedRowChange?.(idx)}
                    animate={
                      isFlashing
                        ? {
                            backgroundColor: [
                              "rgba(251, 191, 36, 0.25)",
                              "rgba(251, 191, 36, 0)",
                            ],
                          }
                        : {}
                    }
                    transition={{ duration: 2, ease: "easeOut" }}
                    className={cn(
                      "transition-colors",
                      isSelected
                        ? "bg-amber-50/40"
                        : "hover:bg-stone-50",
                      isFocused &&
                        "outline outline-2 outline-offset-[-2px] outline-amber-500",
                    )}
                  >
                    {selectable && (
                      <td className="hidden px-4 py-3 md:table-cell">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => selection!.toggle(row.id)}
                          aria-label={`Sélectionner ${row.name}`}
                        />
                      </td>
                    )}
                    {visibleColumns.map((col) => (
                      <td
                        key={col.id}
                        className="px-4 py-3 text-sm text-stone-700"
                      >
                        {renderCell(col.id, row, {
                          onEdit,
                          onView,
                          onDelete,
                          onStockDrawer,
                          editor,
                        })}
                      </td>
                    ))}
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card view */}
      <div className="space-y-3 md:hidden">
        {products.map((row) => {
          const isFlashing = flashingRowIds?.has(row.id) ?? false;
          const editor = activeEditors?.get(row.id);
          return (
            <AnimatePresence key={row.id}>
              <motion.div
                animate={
                  isFlashing
                    ? {
                        backgroundColor: [
                          "rgba(251, 191, 36, 0.25)",
                          "rgba(255, 255, 255, 1)",
                        ],
                      }
                    : {}
                }
                transition={{ duration: 2, ease: "easeOut" }}
                className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-stone-900">
                      {row.name}
                    </span>
                    {row.is_featured && (
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                    )}
                    {editor && <PresenceAvatar editor={editor} />}
                  </div>
                  <BrandBadge brand={row.brand} />
                </div>
              </motion.div>
            </AnimatePresence>
          );
        })}
      </div>

      {/* Pagination */}
      {pagination && onPageChange && (
        <PaginationControls
          pagination={pagination}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cell rendering
// ---------------------------------------------------------------------------

function renderCell(
  columnId: string,
  row: Product,
  ctx: {
    readonly onEdit: (product: Product) => void;
    readonly onView: (product: Product) => void;
    readonly onDelete?: (product: Product) => void;
    readonly onStockDrawer?: (product: Product) => void;
    readonly editor?: { readonly name: string; readonly color: string };
  },
): ReactNode {
  switch (columnId) {
    case "image":
      return (
        <ProductImage
          src={row.image_url}
          alt={`Produit ${row.name}`}
          size="sm"
          className="h-10 w-10 overflow-hidden rounded-md border border-stone-200"
        />
      );
    case "name":
      return (
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-stone-900">{row.name}</span>
          {row.is_featured && (
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
          )}
          {ctx.editor && <PresenceAvatar editor={ctx.editor} />}
        </div>
      );
    case "sku":
      return (
        <span className="font-mono text-xs text-stone-500">{row.sku}</span>
      );
    case "brand":
      return <BrandBadge brand={row.brand} />;
    case "category":
      return (
        <span className="text-sm text-stone-600">
          {CATEGORY_LABEL_MAP[row.category] ?? row.category}
        </span>
      );
    case "price_fcfa":
      return (
        <span className="font-medium text-stone-900">
          {formatFCFA(row.price_fcfa)}
        </span>
      );
    case "stock":
      return <StockCell product={row} />;
    case "threshold":
      return <ThresholdCell product={row} />;
    case "status":
      return <StatusBadge active={row.active} />;
    case "updated_at":
      return (
        <span className="text-xs text-stone-500">
          {new Date(row.updated_at).toLocaleDateString("fr-FR")}
        </span>
      );
    case "actions":
      return (
        <div className="flex items-center gap-1">
          {ctx.onStockDrawer && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                ctx.onStockDrawer!(row);
              }}
              className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
              aria-label={`Ajuster le stock de ${row.name}`}
              title="Stock"
            >
              <Boxes className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              ctx.onEdit(row);
            }}
            className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
            aria-label={`Modifier ${row.name}`}
            title="Modifier"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              ctx.onView(row);
            }}
            className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
            aria-label={`Voir ${row.name}`}
            title="Voir"
          >
            <Eye className="h-4 w-4" />
          </button>
          {ctx.onDelete && row.active && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                ctx.onDelete!(row);
              }}
              className="rounded-md p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600"
              aria-label={`Désactiver ${row.name}`}
              title="Désactiver"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      );
    default:
      return null;
  }
}
